import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Google Analytics 4 + Google Ads dönüşüm takibi.
 *
 * KURULUM (proje kökündeki .env dosyasına; değiştirince yeniden derleyin):
 *   REACT_APP_GA_ID=G-ABC1234XYZ                 → Analytics "Ölçüm Kimliği" (isteğe bağlı)
 *   REACT_APP_GOOGLE_ADS_ID=AW-123456789         → Google Ads "Dönüşüm Kimliği"
 *   REACT_APP_ADS_WHATSAPP_LABEL=AbCdEfGh123     → "WhatsApp tıklaması" dönüşümünün etiketi
 *   REACT_APP_ADS_TELEFON_LABEL=XyZ987654        → "Telefon tıklaması" dönüşümünün etiketi
 *
 * Kimlikler tanımlı değilse bu bileşen hiçbir şey yapmaz — hiçbir istek
 * gönderilmez, hiçbir çerez yazılmaz.
 *
 * Takip edilenler: sitedeki her WhatsApp bağlantısı (wa.me) ve telefon bağlantısı (tel:)
 * tıklaması. Reklamdan gelen müşterinin yazması/araması Google Ads'te "dönüşüm" olarak görünür.
 *
 * NOT: Ölçüm açıldığında ziyaretçi verisi Google'a gönderilir; gizlilik/çerez metni gerekebilir.
 */
const GA_ID = process.env.REACT_APP_GA_ID;
const ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;
const ADS_ETIKET = {
  whatsapp: process.env.REACT_APP_ADS_WHATSAPP_LABEL,
  telefon: process.env.REACT_APP_ADS_TELEFON_LABEL,
};
const ILK_KIMLIK = GA_ID || ADS_ID;

// Tıklanan bağlantının türü: WhatsApp, telefon ya da takip dışı
const baglantiTuru = (href = "") => {
  if (/^https:\/\/(wa\.me|api\.whatsapp\.com)\//.test(href)) return "whatsapp";
  if (href.startsWith("tel:")) return "telefon";
  return null;
};

const Analytics = () => {
  const { pathname, search } = useLocation();
  const loaded = useRef(false);

  // gtag betiğini bir kez yükle
  useEffect(() => {
    if (!ILK_KIMLIK || loaded.current) return;
    loaded.current = true;

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${ILK_KIMLIK}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    // Sayfa görüntülemelerini aşağıda kendimiz gönderiyoruz
    if (GA_ID) window.gtag("config", GA_ID, { send_page_view: false });
    if (ADS_ID) window.gtag("config", ADS_ID);
  }, []);

  // Rota değiştikçe sayfa görüntüleme gönder
  useEffect(() => {
    if (!GA_ID || !window.gtag) return;

    window.gtag("event", "page_view", {
      page_path: pathname + search,
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [pathname, search]);

  // WhatsApp ve telefon tıklamaları: tek dinleyici, sitedeki bütün bağlantıları kapsar
  useEffect(() => {
    if (!ILK_KIMLIK) return;
    const tikla = (e) => {
      const a = e.target.closest?.("a[href]");
      const tur = a && baglantiTuru(a.getAttribute("href"));
      if (!tur || !window.gtag) return;
      if (GA_ID) {
        window.gtag("event", tur === "whatsapp" ? "whatsapp_tiklama" : "telefon_tiklama", {
          sayfa: window.location.pathname,
        });
      }
      if (ADS_ID && ADS_ETIKET[tur]) {
        window.gtag("event", "conversion", { send_to: `${ADS_ID}/${ADS_ETIKET[tur]}` });
      }
    };
    document.addEventListener("click", tikla, true);
    return () => document.removeEventListener("click", tikla, true);
  }, []);

  return null;
};

export default Analytics;
